import clsx from "clsx";
import Image from "next/image";
import type { ReactNode } from "react";

import arrowIcon from "./icon_arrow_right_beige.svg";

type Direction = "vertical" | "horizontal" | "auto";

export type StepByStepGuideInstruction = {
  description: string | ReactNode;
};

type ItemType = (StepByStepGuideInstruction & { step: number }) | undefined;

const styles = {
  direction: {
    vertical: "flex-col",
    horizontal: "flex-row",
    auto: "flex-col md:flex-row",
  },
  arrow: {
    vertical: "rotate-90 px-0 py-2",
    horizontal: "md:rotate-0 md:px-2 md:py-0",
    auto: "rotate-90 px-0 py-2 md:rotate-0 md:px-2 md:py-0",
  },
};

/**
 * どんな手順で設定等を行うかを示すコンポーネント
 *
 * @param instructions 手順の説明
 * @param direction 表示方向（`"auto"` はレスポンシブ）
 * @param stepLabel ステップ番号を表示するかどうか
 */
export function StepByStepGuide({
  instructions,
  direction = "auto",
  stepLabel = false,
}: {
  instructions: (string | ReactNode)[];
  direction?: Direction;
  stepLabel?: boolean;
}) {
  const instructionsWithStep = instructions.map((instruction, index) => ({
    description: instruction,
    step: index + 1,
  }));

  const items: ItemType[] = Array.from(
    { length: instructions.length * 2 - 1 },
    (_, i) => (i % 2 === 0 ? instructionsWithStep[i / 2] : undefined),
  );

  const directionStyle = styles.direction[direction];
  const arrowStyle = styles.arrow[direction];

  return (
    <div
      className={clsx(
        "flex items-stretch justify-between justify-items-stretch",
        directionStyle,
      )}
    >
      {items.map((item, index) =>
        item ? (
          <div
            key={index}
            className={clsx(
              "border-sbeige border bg-white px-4 py-2",
              // eslint-disable-next-line better-tailwindcss/no-unknown-classes
              `basis-1/${instructions.length + 1}`,
              direction === "auto" &&
                "flex-row items-center md:flex-col md:items-start",
              direction === "vertical" && "flex-row items-center",
              direction === "horizontal" && "flex-col items-start",
            )}
          >
            {stepLabel && (
              <div
                className={clsx(
                  "shrink-0 text-[18px]",
                  direction === "auto" && "mr-4 mb-0 md:mr-0 md:mb-2",
                  direction === "vertical" && "mr-4 mb-0",
                  direction === "horizontal" && "mr-0 mb-2",
                )}
              >
                Step {item.step}
              </div>
            )}
            <div className="text-[12px]">{item.description}</div>
          </div>
        ) : (
          <div key={index} className={clsx("flex justify-center", arrowStyle)}>
            <Image src={arrowIcon} alt="next" />
          </div>
        ),
      )}
    </div>
  );
}
